const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class YurinoxHeader extends HTMLElement {
  connectedCallback() {
    this.menuDrawer = this.querySelector('[data-yx-menu-drawer]');
    this.menuTrigger = this.querySelector('[data-yx-menu-trigger]');
    this.menuPanel = this.querySelector('[data-yx-menu-panel]');
    this.menuPreviewPanel = this.querySelector('.yx-site-header__menu-panel-desktop');
    this.menuPreviewImage = this.querySelector('[data-yx-menu-preview-image]');
    this.menuPreviewBridge = this.querySelector('[data-yx-menu-preview-bridge]');
    this.menuPreviewLinks = this.querySelector('.yx-site-header__menu-preview-links');
    this.menuPreviewLinkGroups = [...this.querySelectorAll('[data-yx-menu-preview-group]')];
    this.menuPreviewTriggers = this.querySelectorAll('.yx-site-header__menu-primary a');
    this.desktopMenuMedia = window.matchMedia('(min-width: 1200px)');
    this.tabletMenuMedia = window.matchMedia('(min-width: 750px) and (max-width: 1199px)');
    this.mobileMenuMedia = window.matchMedia('(max-width: 749px)');
    this.mobileMenuSections = this.querySelectorAll('.yx-site-header__mobile-menu-section');
    this.mobileMenuClose = this.querySelector('[data-yx-mobile-menu-close]');
    this.tabletMenuClose = this.querySelector('[data-yx-tablet-menu-close]');
    this.workwearMenu = this.querySelector('[data-yx-workwear-menu]');
    this.workwearTrigger = this.querySelector('[data-yx-workwear-trigger]');
    this.workwearPanel = this.querySelector('[data-yx-workwear-panel]');
    this.workwearClose = this.querySelector('[data-yx-workwear-close]');
    this.workwearSystemMenu = this.querySelector('[data-yx-workwear-system-menu]');
    this.workwearSystemTrigger = this.querySelector('[data-yx-workwear-system-trigger]');
    this.workwearSystemPanel = this.querySelector('[data-yx-workwear-system-panel]');
    this.collectionsMenu = this.querySelector('[data-yx-collections-menu]');
    this.collectionsTrigger = this.querySelector('[data-yx-collections-trigger]');
    this.collectionsPanel = this.querySelector('[data-yx-collections-panel]');
    this.shopifyMenus = [...this.querySelectorAll('[data-yx-shopify-menu]')];
    this.searchTrigger = this.querySelector('[data-yx-search-trigger]');
    this.announceSlides = [...this.querySelectorAll('[data-yx-announce-slide]')];
    this.announcePrev = this.querySelector('[data-yx-announce-prev]');
    this.announceNext = this.querySelector('[data-yx-announce-next]');
    this.announceSlideIndex = Math.max(0, this.announceSlides.findIndex((slide) => !slide.hidden));
    this.languageSwitcher = this.querySelector('.yx-site-header__language-switcher');
    this.languageTrigger = this.querySelector('[data-yx-language-trigger]');
    this.languageMenu = this.querySelector('[data-yx-language-menu]');
    this.mobileLanguageInput = this.querySelector('#YxAnnounceLocalizationMobile select[name="language_code"]');
    this.languageOptions = [...this.querySelectorAll('[data-yx-language-option]')];
    this.updateLanguageReturnPaths();
    this.headerGroup = this.closest('#header-group');
    this.searchModal = null;
    this.searchDialog = null;

    this.headerMetricsObserver = new ResizeObserver(this.syncHeaderMetrics);
    this.headerMetricsObserver.observe(this);
    if (this.headerGroup) this.headerMetricsObserver.observe(this.headerGroup);
    this.syncHeaderMetrics();

    this.menuTrigger?.setAttribute('aria-expanded', String(Boolean(this.menuDrawer?.open)));
    this.menuTrigger?.setAttribute('aria-controls', 'yx-header-menu-panel');
    this.menuPanel?.setAttribute('id', 'yx-header-menu-panel');
    this.workwearTrigger?.setAttribute('aria-expanded', String(Boolean(this.workwearMenu?.open)));
    this.workwearTrigger?.setAttribute('aria-controls', 'yx-workwear-menu-panel');
    this.workwearSystemTrigger?.setAttribute('aria-expanded', String(Boolean(this.workwearSystemMenu?.open)));
    this.workwearSystemTrigger?.setAttribute('aria-controls', 'yx-workwear-system-menu-panel');
    this.collectionsTrigger?.setAttribute('aria-expanded', String(Boolean(this.collectionsMenu?.open)));
    this.collectionsTrigger?.setAttribute('aria-controls', 'yx-collections-menu-panel');

    this.menuDrawer?.addEventListener('toggle', this.onMenuToggle);
    this.menuDrawer?.addEventListener('keydown', this.onMenuKeydown);
    this.mobileMenuSections.forEach((section) => section.addEventListener('toggle', this.onMobileMenuSectionToggle));
    this.mobileMenuClose?.addEventListener('click', this.onMobileMenuClose);
    this.tabletMenuClose?.addEventListener('click', this.onTabletMenuClose);
    this.menuPreviewTriggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', this.onMenuPreviewEnter);
      trigger.addEventListener('pointerleave', this.onMenuPreviewLeave);
      trigger.addEventListener('focus', this.onMenuPreviewEnter);
      trigger.addEventListener('click', this.onTabletMenuPreviewClick);
    });
    this.menuPreviewPanel?.addEventListener('pointerenter', this.onMenuPreviewKeepAlive);
    this.menuPreviewPanel?.addEventListener('pointerleave', this.onMenuPreviewLeave);
    this.menuPreviewBridge?.addEventListener('pointerenter', this.onMenuPreviewKeepAlive);
    this.menuPreviewBridge?.addEventListener('pointerleave', this.onMenuPreviewLeave);
    this.menuPreviewLinks?.addEventListener('pointerenter', this.onMenuPreviewKeepAlive);
    this.menuPreviewLinks?.addEventListener('pointerleave', this.onMenuPreviewLeave);
    this.workwearMenu?.addEventListener('toggle', this.onWorkwearToggle);
    this.workwearMenu?.addEventListener('keydown', this.onWorkwearKeydown);
    this.workwearClose?.addEventListener('click', this.onWorkwearClose);
    this.workwearSystemMenu?.addEventListener('toggle', this.onWorkwearSystemToggle);
    this.workwearSystemMenu?.addEventListener('keydown', this.onWorkwearSystemKeydown);
    this.collectionsMenu?.addEventListener('toggle', this.onCollectionsToggle);
    this.collectionsMenu?.addEventListener('keydown', this.onCollectionsKeydown);
    this.shopifyMenus.forEach((menu) => {
      menu.querySelector('summary')?.setAttribute('aria-expanded', String(menu.open));
      menu.addEventListener('toggle', this.onShopifyMenuToggle);
      menu.addEventListener('keydown', this.onShopifyMenuKeydown);
    });
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    this.searchTrigger?.addEventListener('click', this.onSearchTriggerClick);
    this.announcePrev?.addEventListener('click', this.onAnnouncementPrev);
    this.announceNext?.addEventListener('click', this.onAnnouncementNext);
    this.languageTrigger?.addEventListener('click', this.onLanguageToggle);
    this.languageSwitcher?.addEventListener('submit', this.prepareLanguageNavigation);
    this.mobileLanguageInput?.addEventListener('change', this.onLocalPreviewLanguageChange, { capture: true });
    this.languageOptions.forEach((option) => option.addEventListener('click', this.onLocalPreviewLanguageClick));
    this.syncAnnouncementControls();
    this.initSearchModal();
    window.addEventListener('DOMContentLoaded', this.initSearchModal);
  }

  disconnectedCallback() {
    this.menuDrawer?.removeEventListener('toggle', this.onMenuToggle);
    this.menuDrawer?.removeEventListener('keydown', this.onMenuKeydown);
    this.mobileMenuSections?.forEach((section) => section.removeEventListener('toggle', this.onMobileMenuSectionToggle));
    this.mobileMenuClose?.removeEventListener('click', this.onMobileMenuClose);
    this.tabletMenuClose?.removeEventListener('click', this.onTabletMenuClose);
    this.menuPreviewTriggers?.forEach((trigger) => {
      trigger.removeEventListener('pointerenter', this.onMenuPreviewEnter);
      trigger.removeEventListener('pointerleave', this.onMenuPreviewLeave);
      trigger.removeEventListener('focus', this.onMenuPreviewEnter);
      trigger.removeEventListener('click', this.onTabletMenuPreviewClick);
    });
    this.menuPreviewPanel?.removeEventListener('pointerenter', this.onMenuPreviewKeepAlive);
    this.menuPreviewPanel?.removeEventListener('pointerleave', this.onMenuPreviewLeave);
    this.menuPreviewBridge?.removeEventListener('pointerenter', this.onMenuPreviewKeepAlive);
    this.menuPreviewBridge?.removeEventListener('pointerleave', this.onMenuPreviewLeave);
    this.menuPreviewLinks?.removeEventListener('pointerenter', this.onMenuPreviewKeepAlive);
    this.menuPreviewLinks?.removeEventListener('pointerleave', this.onMenuPreviewLeave);
    this.workwearMenu?.removeEventListener('toggle', this.onWorkwearToggle);
    this.workwearMenu?.removeEventListener('keydown', this.onWorkwearKeydown);
    this.workwearClose?.removeEventListener('click', this.onWorkwearClose);
    this.workwearSystemMenu?.removeEventListener('toggle', this.onWorkwearSystemToggle);
    this.workwearSystemMenu?.removeEventListener('keydown', this.onWorkwearSystemKeydown);
    this.collectionsMenu?.removeEventListener('toggle', this.onCollectionsToggle);
    this.collectionsMenu?.removeEventListener('keydown', this.onCollectionsKeydown);
    this.shopifyMenus?.forEach((menu) => {
      menu.removeEventListener('toggle', this.onShopifyMenuToggle);
      menu.removeEventListener('keydown', this.onShopifyMenuKeydown);
    });
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('scroll', this.onWindowScroll);
    this.searchTrigger?.removeEventListener('click', this.onSearchTriggerClick);
    this.announcePrev?.removeEventListener('click', this.onAnnouncementPrev);
    this.announceNext?.removeEventListener('click', this.onAnnouncementNext);
    this.languageTrigger?.removeEventListener('click', this.onLanguageToggle);
    this.languageSwitcher?.removeEventListener('submit', this.prepareLanguageNavigation);
    this.mobileLanguageInput?.removeEventListener('change', this.onLocalPreviewLanguageChange, { capture: true });
    this.languageOptions?.forEach((option) => option.removeEventListener('click', this.onLocalPreviewLanguageClick));
    window.removeEventListener('DOMContentLoaded', this.initSearchModal);
    this.searchObserver?.disconnect();
    this.headerMetricsObserver?.disconnect();
    window.clearTimeout(this.menuPreviewEnterTimer);
    window.clearTimeout(this.menuPreviewLeaveTimer);
  }

  onMenuToggle = () => {
    const isOpen = Boolean(this.menuDrawer?.open);
    this.menuTrigger?.setAttribute('aria-expanded', String(isOpen));
    if (isOpen) {
      if (!this.mobileMenuMedia?.matches) this.hydrateDeferredImages(this.menuPanel);
      this.closeWorkwear(false);
      this.closeWorkwearSystem(false);
      this.closeCollections(false);
    }
    if (isOpen && this.mobileMenuMedia?.matches) {
      this.mobileMenuSections.forEach((section, index) => {
        section.open = index === 0;
      });
    }
    if (isOpen && this.tabletMenuMedia?.matches) this.closeMenuPreview();
    if (!isOpen) this.closeMenuPreview();
  };

  onMobileMenuSectionToggle = (event) => {
    const section = event.currentTarget;
    if (!this.mobileMenuMedia?.matches || !section?.open) return;
    this.mobileMenuSections.forEach((otherSection) => {
      if (otherSection !== section) otherSection.open = false;
    });
  };

  onMobileMenuClose = () => {
    this.closeMenu(true);
  };

  onTabletMenuClose = () => {
    this.closeMenu(true);
  };

  onTabletMenuPreviewClick = (event) => {
    if (!this.tabletMenuMedia?.matches || !this.menuPreviewPanel) return;

    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLAnchorElement)) return;

    const isCurrent = trigger.classList.contains('is-tablet-preview-current');
    if (isCurrent && this.menuPreviewPanel.classList.contains('is-tablet-preview-active')) return;

    event.preventDefault();
    const imageSource = trigger.dataset.yxMenuPreviewImage;
    if (imageSource && this.menuPreviewImage?.getAttribute('src') !== imageSource) {
      this.menuPreviewImage.src = imageSource;
    }
    this.showMenuPreviewLinks(trigger.dataset.yxMenuPreviewKey);

    this.menuPreviewTriggers.forEach((item) => item.classList.remove('is-tablet-preview-current'));
    trigger.classList.add('is-tablet-preview-current');
    this.menuPreviewPanel.classList.add('is-tablet-preview-active');
  };

  onMenuPreviewEnter = (event) => {
    if (!this.desktopMenuMedia?.matches) return;

    const trigger = event.currentTarget;
    const activatePreview = () => {
      const imageSource = trigger?.dataset.yxMenuPreviewImage;
      if (imageSource && this.menuPreviewImage?.getAttribute('src') !== imageSource) {
        this.menuPreviewImage.src = imageSource;
      }
      this.showMenuPreviewLinks(trigger?.dataset.yxMenuPreviewKey);

      window.clearTimeout(this.menuPreviewLeaveTimer);
      this.menuPreviewPanel?.classList.add('is-preview-active');
    };

    window.clearTimeout(this.menuPreviewEnterTimer);
    if (event.type === 'focus') {
      activatePreview();
      return;
    }

    this.menuPreviewEnterTimer = window.setTimeout(() => {
      if (trigger?.matches(':hover')) activatePreview();
    }, 220);
  };

  onMenuPreviewKeepAlive = () => {
    if (!this.desktopMenuMedia?.matches) return;
    if (!this.menuPreviewPanel?.classList.contains('is-preview-active')) return;

    window.clearTimeout(this.menuPreviewLeaveTimer);
  };

  showMenuPreviewLinks = (key) => {
    if (!key) return;
    this.menuPreviewLinkGroups?.forEach((group) => {
      group.hidden = group.dataset.yxMenuPreviewGroup !== key;
    });
  };

  onMenuPreviewLeave = () => {
    window.clearTimeout(this.menuPreviewEnterTimer);
    window.clearTimeout(this.menuPreviewLeaveTimer);
    this.menuPreviewLeaveTimer = window.setTimeout(() => {
      const hoveringTrigger = [...this.menuPreviewTriggers].some((trigger) => trigger.matches(':hover'));
      const hoveringPanel = this.menuPreviewPanel?.matches(':hover');
      const hoveringBridge = this.menuPreviewBridge?.matches(':hover');
      const hoveringLinks = this.menuPreviewLinks?.matches(':hover');

      if (!hoveringTrigger && !hoveringPanel && !hoveringBridge && !hoveringLinks) this.closeMenuPreview();
    }, 480);
  };

  onWorkwearToggle = () => {
    const isOpen = Boolean(this.workwearMenu?.open);
    this.workwearTrigger?.setAttribute('aria-expanded', String(isOpen));
    if (isOpen) {
      this.hydrateDeferredImages(this.workwearPanel);
      this.closeMenu(false);
      this.closeWorkwearSystem(false);
      this.closeCollections(false);
      this.shopifyMenus.forEach((menu) => { menu.open = false; });
    }
  };

  onWorkwearSystemToggle = () => {
    const isOpen = Boolean(this.workwearSystemMenu?.open);
    this.workwearSystemTrigger?.setAttribute('aria-expanded', String(isOpen));
    if (isOpen) {
      this.hydrateDeferredImages(this.workwearSystemPanel);
      this.closeMenu(false);
      this.closeWorkwear(false);
      this.closeCollections(false);
      this.shopifyMenus.forEach((menu) => { menu.open = false; });
    }
  };

  onCollectionsToggle = () => {
    const isOpen = Boolean(this.collectionsMenu?.open);
    this.collectionsTrigger?.setAttribute('aria-expanded', String(isOpen));
    if (isOpen) {
      this.hydrateDeferredImages(this.collectionsPanel);
      this.closeMenu(false);
      this.closeWorkwear(false);
      this.closeWorkwearSystem(false);
      this.shopifyMenus.forEach((menu) => { menu.open = false; });
    }
  };

  onShopifyMenuToggle = (event) => {
    const menu = event.currentTarget;
    if (!(menu instanceof HTMLDetailsElement)) return;
    menu.querySelector('summary')?.setAttribute('aria-expanded', String(menu.open));
    if (!menu.open) return;

    this.hydrateDeferredImages(menu);
    this.closeMenu(false);
    this.closeWorkwear(false);
    this.closeWorkwearSystem(false);
    this.closeCollections(false);
    this.shopifyMenus.forEach((otherMenu) => {
      if (otherMenu !== menu) otherMenu.open = false;
    });
  };

  onShopifyMenuKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const menu = event.currentTarget;
    if (!(menu instanceof HTMLDetailsElement) || !menu.open) return;
    event.preventDefault();
    menu.open = false;
    menu.querySelector('summary')?.focus();
  };

  onMenuKeydown = (event) => {
    if (!this.menuDrawer?.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu(true);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = this.getMenuFocusables();
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  onWorkwearKeydown = (event) => {
    if (!this.workwearMenu?.open || event.key !== 'Escape') return;
    event.preventDefault();
    this.closeWorkwear(true);
  };

  onWorkwearClose = () => {
    this.closeWorkwear(true);
  };

  onWorkwearSystemKeydown = (event) => {
    if (!this.workwearSystemMenu?.open || event.key !== 'Escape') return;
    event.preventDefault();
    this.closeWorkwearSystem(true);
  };

  onCollectionsKeydown = (event) => {
    if (!this.collectionsMenu?.open || event.key !== 'Escape') return;
    event.preventDefault();
    this.closeCollections(true);
  };

  onWindowScroll = () => {
    // A dropdown belongs to its Workwear trigger.  Leaving it fixed over a
    // scrolled page would detach it from the header and obscure page content.
    this.closeWorkwear(false);
    this.closeWorkwearSystem(false);
    this.closeCollections(false);
    this.shopifyMenus?.forEach((menu) => { menu.open = false; });
  };

  syncHeaderMetrics = () => {
    const headerHeight = Math.round(this.getBoundingClientRect().height);
    const headerGroupHeight = Math.round(this.headerGroup?.getBoundingClientRect().height || headerHeight);

    document.body.style.setProperty('--header-height', `${headerHeight}px`);
    document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
  };

  hydrateDeferredImages(container) {
    container?.querySelectorAll('img[data-yx-deferred-src]').forEach((image) => {
      const source = image.dataset.yxDeferredSrc;
      if (!source) return;
      image.src = source;
      image.removeAttribute('data-yx-deferred-src');
    });
  }

  onDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.menuDrawer?.open && !this.menuDrawer.contains(event.target)) this.closeMenu(false);
    if (this.workwearMenu?.open && !this.workwearMenu.contains(event.target)) this.closeWorkwear(false);
    if (
      this.workwearSystemMenu?.open &&
      (!this.workwearSystemMenu.contains(event.target) || event.target === this.workwearSystemPanel)
    ) {
      this.closeWorkwearSystem(false);
    }
    if (
      this.collectionsMenu?.open &&
      (!this.collectionsMenu.contains(event.target) || event.target === this.collectionsPanel)
    ) {
      this.closeCollections(false);
    }
    this.shopifyMenus?.forEach((menu) => {
      const panel = menu.querySelector('.yx-site-header__feature-panel');
      if (menu.open && (!menu.contains(event.target) || event.target === panel)) menu.open = false;
    });
    if (this.languageSwitcher?.classList.contains('is-open') && !this.languageSwitcher.contains(event.target)) {
      this.setLanguageOpen(false);
    }
  };

  onSearchTriggerClick = () => {
    this.lastSearchTrigger = this.searchTrigger;
    this.searchTrigger?.setAttribute('aria-expanded', 'true');
    this.initSearchModal();
    requestAnimationFrame(() => this.searchDialog?.querySelector('input[type="search"]')?.focus());
  };

  onAnnouncementPrev = () => {
    this.setAnnouncementSlide(this.announceSlideIndex - 1);
  };

  onAnnouncementNext = () => {
    this.setAnnouncementSlide(this.announceSlideIndex + 1);
  };

  onLanguageToggle = () => {
    this.setLanguageOpen(!this.languageSwitcher?.classList.contains('is-open'));
  };

  onLocalPreviewLanguageChange = (event) => {
    this.prepareLanguageNavigation();
    if (!this.isLocalPreview()) return;

    const option = event.target?.selectedOptions?.[0];
    if (!option) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.navigateToLanguageRoot(option.dataset.yxLanguageRoot);
  };

  onLocalPreviewLanguageClick = (event) => {
    this.prepareLanguageNavigation();
    if (!this.isLocalPreview()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.navigateToLanguageRoot(event.currentTarget?.dataset.yxLanguageRoot);
  };

  isLocalPreview() {
    return ['127.0.0.1', 'localhost', '0.0.0.0'].includes(window.location.hostname);
  }

  getLanguageReturnURL() {
    const url = new URL(window.location.href);
    // Cart updates re-render the header with a Section Rendering API URL.
    // Language changes must return to the full page, retaining its real query.
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    return url;
  }

  updateLanguageReturnPaths = () => {
    const url = this.getLanguageReturnURL();
    this.querySelectorAll('form[id^="YxAnnounceLocalization"] input[name="return_to"]').forEach((input) => {
      input.value = `${url.pathname}${url.search}${url.hash}`;
    });
  };

  prepareLanguageNavigation = () => {
    this.updateLanguageReturnPaths();
    // Locale redirects must not leave the next page waiting for an outgoing
    // document transition. Ordinary storefront navigation keeps its animation.
    window.addEventListener('pageswap', this.skipLanguageViewTransition, { once: true });
  };

  skipLanguageViewTransition = (event) => {
    event.viewTransition?.skipTransition();
  };

  navigateToLanguageRoot(targetRoot) {
    if (!targetRoot) return;

    const normalizeRoot = (root) => {
      const normalized = `/${String(root || '').replace(/^\/+|\/+$/g, '')}`;
      return normalized === '/' ? '/' : normalized;
    };
    const currentRoot = normalizeRoot(this.dataset.yxLanguageRoot);
    const nextRoot = normalizeRoot(targetRoot);
    let pagePath = window.location.pathname || '/';

    if (currentRoot !== '/' && (pagePath === currentRoot || pagePath.startsWith(`${currentRoot}/`))) {
      pagePath = pagePath.slice(currentRoot.length) || '/';
    }

    const nextPath = nextRoot === '/'
      ? pagePath
      : `${nextRoot}${pagePath === '/' ? '' : pagePath}`;
    const url = this.getLanguageReturnURL();
    window.location.assign(`${nextPath}${url.search}${url.hash}`);
  }

  setLanguageOpen(isOpen) {
    if (!this.languageSwitcher || !this.languageTrigger || !this.languageMenu) return;

    this.languageSwitcher.classList.toggle('is-open', isOpen);
    this.languageTrigger.setAttribute('aria-expanded', String(isOpen));
    this.languageMenu.hidden = !isOpen;
  }

  setAnnouncementSlide(index) {
    if (this.announceSlides.length < 2) return;

    this.announceSlideIndex = Math.min(Math.max(index, 0), this.announceSlides.length - 1);
    this.announceSlides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== this.announceSlideIndex;
    });
    this.syncAnnouncementControls();
  }

  syncAnnouncementControls() {
    if (this.announceSlides.length < 2) {
      this.announcePrev?.setAttribute('hidden', '');
      this.announceNext?.setAttribute('hidden', '');
      return;
    }

    this.announcePrev?.toggleAttribute('disabled', this.announceSlideIndex === 0);
    this.announceNext?.toggleAttribute('disabled', this.announceSlideIndex === this.announceSlides.length - 1);
  }

  initSearchModal = () => {
    if (this.searchDialog) return;
    this.searchModal = document.getElementById('search-modal');
    this.searchDialog = this.searchModal?.querySelector('dialog');
    if (!this.searchDialog) return;

    this.searchObserver = new MutationObserver(() => {
      const isOpen = Boolean(this.searchDialog?.open);
      this.searchTrigger?.setAttribute('aria-expanded', String(isOpen));
      if (!isOpen && this.lastSearchTrigger) this.lastSearchTrigger.focus();
    });
    this.searchObserver.observe(this.searchDialog, { attributes: true, attributeFilter: ['open'] });
  };

  closeMenu(restoreFocus) {
    if (!this.menuDrawer) return;
    this.menuDrawer.open = false;
    this.menuTrigger?.setAttribute('aria-expanded', 'false');
    this.closeMenuPreview();
    if (restoreFocus) this.menuTrigger?.focus();
  }

  closeMenuPreview() {
    window.clearTimeout(this.menuPreviewLeaveTimer);
    this.menuPreviewPanel?.classList.remove('is-preview-active');
    this.menuPreviewPanel?.classList.remove('is-tablet-preview-active');
    this.menuPreviewTriggers.forEach((trigger) => trigger.classList.remove('is-tablet-preview-current'));
  }

  closeWorkwear(restoreFocus) {
    if (!this.workwearMenu?.open) return;
    this.workwearMenu.open = false;
    this.workwearTrigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.workwearTrigger?.focus();
  }

  closeWorkwearSystem(restoreFocus) {
    if (!this.workwearSystemMenu?.open) return;
    this.workwearSystemMenu.open = false;
    this.workwearSystemTrigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.workwearSystemTrigger?.focus();
  }

  closeCollections(restoreFocus) {
    if (!this.collectionsMenu?.open) return;
    this.collectionsMenu.open = false;
    this.collectionsTrigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.collectionsTrigger?.focus();
  }

  getMenuFocusables() {
    if (!this.menuDrawer) return [];
    return [...this.menuDrawer.querySelectorAll(focusableSelector)].filter((element) => {
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
    });
  }
}

if (!customElements.get('yurinox-header')) {
  customElements.define('yurinox-header', YurinoxHeader);
}
